/**
 * English message dictionary (US + Canada English).
 * French mirrors this shape in fr.ts.
 */

export type MessageTree = {
  [key: string]: string | readonly string[] | MessageTree;
};

export const en = {
  nav: {
    apps: "Apps",
    webSupport: "Web Support",
    hosting: "Hosting",
    help: "Help",
    helpCenter: "Help Center",
    faqs: "FAQs",
    clients: "Clients",
    contact: "Contact",
    support: "Support",
    navigation: "Navigation",
    closeMenu: "Close menu",
    homeAria: "CloudAxe home",
    hipaaForms: "HIPAA Compliant Forms",
    overview: "Overview",
    getStarted: "Get started",
    pricing: "Pricing",
    developers: "Developers",
    api: "API",
    wordpress: "WordPress",
    joomla: "Joomla",
    faq: "FAQ",
    security: "Security",
    customApps: "Custom Apps",
    proudlyCanadian: "Proudly Canadian",
    langEn: "EN",
    langFr: "FR",
    switchToEnglish: "Switch to English",
    switchToFrench: "Passer au français",
  },
  footer: {
    tagline:
      "Apps, Web Support, and Hosting for small business. Clear diagnosis, clean fix, lasting prevention.",
    services: "Services",
    help: "Help",
    clients: "Clients",
    clientPortal: "Client portal",
    apps: "Apps",
    webSupport: "Web Support",
    hosting: "Hosting",
    helpCenter: "Help Center",
    faq: "FAQ",
    contact: "Contact",
    privacy: "Privacy",
    terms: "Terms",
    copyright: "CloudAxe — Break Thru.",
    breakThru: "Break Thru",
  },
  banner: {
    axcIsNow: "AXC is now",
  },
  privacy: {
    aria: "Privacy consent",
    body:
      "We use cookies and similar technologies to run this site and understand how it is used. You can accept or decline non-essential use. See our",
    policy: "Privacy Policy",
    decline: "Decline",
    accept: "Accept",
  },
  contactForm: {
    title: "Send a message",
    subtitle: "We typically respond within one business day.",
    success: "Thanks — your message was sent. We'll get back to you soon.",
    name: "Name",
    email: "Email",
    message: "What do you need help with?",
    send: "Send message",
    sending: "Sending…",
    errorFallback: "Unable to send your message. Please try again.",
  },
  home: {
    subtitle: "Break Thru",
    eyebrow: "Expert Web support",
    uNext: "U Next?",
    heroLine1: "Break Thru the noise —",
    heroLine2Before: "Web Support",
    heroLine2Mid: "for small business that",
    heroLine2Accent: "delivers.",
    ctaSupport: "Get Tech Support",
    ctaHipaa: "Get Hipaa Forms",
    ctaHosting: "Get Hosting",
    servicesEyebrow: "What we do",
    servicesTitle: "What does CloudAxe do for small businesses?",
    servicesIntro:
      "CloudAxe provides three services for small businesses: purpose-built apps (HIPAA-compliant forms, event tools, and custom apps), expert web support for WordPress, Next.js, and Joomla, and managed hosting with backups, monitoring, and staging. Start where it matters most.",
    getQuote: "Get a quote",
    serviceAppsTitle: "Apps",
    serviceAppsDesc:
      "Purpose-built apps that streamline how your small business works—forms, events, and custom tools that actually match your workflows.",
    serviceAppsBullets: [
      "HIPAA-compliant forms",
      "Event and registration tools",
      "Custom apps for your stack",
    ],
    serviceSupportTitle: "Web Support",
    serviceSupportDesc:
      "Over 20 years experience specializing in WordPress, Next.js, and Joomla. Troubleshooting, updates, security, and performance—when you need it.",
    serviceSupportBullets: [
      "WordPress, Next.js, Joomla",
      "Troubleshooting & fixes",
      "Security & performance",
    ],
    serviceHostingTitle: "Hosting",
    serviceHostingDesc:
      "Managed hosting that prioritizes uptime, speed, and clear communication. A clean stack, monitored 24/7, with backups you can actually restore.",
    serviceHostingBullets: [
      "Backups + restore testing",
      "Monitoring + alerts",
      "Staging environments",
    ],
    providersEyebrow: "Trusted integrations",
    providersTitle: "Providers",
    providersIntro: "We integrate with the platforms you already use.",
    outcomesEyebrow: "Outcomes",
    outcomesTitle: "Break Thru outcomes",
    outcomesIntro: "Clear diagnosis, clean fix, then prevention. That's the whole point.",
    outcome1t: "Incidents handled",
    outcome1d: "Triage quickly, communicate clearly, fix root cause.",
    outcome2t: "Faster sites",
    outcome2d: "Caching, CDN strategy, DB tuning, and image optimization.",
    outcome3t: "Cleaner security",
    outcome3d: "Updates, hardened configs, malware cleanup, monitoring.",
    techEyebrow: "Modern stacks",
    techTitle: "Technologies",
    techIntro: "We build and support modern stacks you can rely on.",
    ctaEyebrow: "Ready when you are",
    ctaTitle: "Ready to Break Thru?",
    ctaBody:
      "Tell us about your site, your app idea, or the issue you're stuck on. We'll triage it together.",
    ctaCall: "Book a quick call",
    ctaHelp: "Visit help center",
    metaTitle: "Web Support, Apps & Hosting for Small Business",
    metaDescription:
      "CloudAxe helps small businesses break through: expert web support for WordPress, Next.js, and Joomla, purpose-built apps like HIPAA-compliant forms, and managed hosting with backups you can actually restore.",
  },
  webSupport: {
    subtitle: "Web Support",
    eyebrow: "Expert Web support",
    uNext: "U Next?",
    outcomesEyebrow: "Outcomes",
    outcomesTitle: "What does CloudAxe web support cover?",
    outcomesIntro:
      "CloudAxe web support covers updates, troubleshooting, security hardening, and performance tuning for WordPress, Next.js, and Joomla sites — plus DNS, SSL, and email issues when they affect your site. One-off fixes and ongoing retainers are both available.",
    outcome1t: "Incidents handled",
    outcome1d: "Triage quickly, communicate clearly, fix root cause.",
    outcome2t: "Faster sites",
    outcome2d: "Caching, CDN strategy, DB tuning, and image optimization.",
    outcome3t: "Cleaner security",
    outcome3d: "Updates, hardened configs, malware cleanup, monitoring.",
    metaTitle: "Web Support for WordPress, Next.js & Joomla",
    metaDescription:
      "Submit a web support request in seconds. WordPress, Next.js, Joomla, and more. Over 20 years of experience — get started with our quick intake form.",
  },
  hosting: {
    subtitle: "Hosting",
    eyebrow: "VPS & shared hosting",
    title: "Small Business Hosting",
    introBefore: "CloudAxe managed hosting starts at",
    introMid: "for shared plans and",
    introAfter:
      "for VPS. Choose dedicated VPS resources for full control and scale, or shared hosting for a managed, cost-effective stack. Both include backups with restore testing, uptime monitoring, and support.",
    highlightBackups: "Backups",
    highlightBackupsD: "Automated + restore tested",
    highlightSsl: "SSL",
    highlightSslD: "Auto renewal & expiry alerts",
    highlightSpeed: "Speed",
    highlightSpeedD: "Caching + CDN options",
    highlightSecurity: "Security",
    highlightSecurityD: "Hardening + monitoring",
    vpsWhatTitle: "What is a VPS server?",
    vpsWhatBody:
      "A virtual private server (VPS) is a slice of a physical machine with dedicated CPU and RAM assigned to you. You get root access and predictable performance without sharing resources with other tenants like on shared hosting. Compared to bare metal, a VPS is cheaper and quicker to provision while still giving you full control; compared to shared hosting, you get isolation, better performance under load, and the ability to run custom software and scale when you need it.",
    vpsTitle: "VPS servers",
    vpsIntro:
      "Dedicated CPU and RAM, root access, and a managed stack. Best for apps that need predictable performance and room to grow.",
    sharedTitle: "Shared hosting",
    sharedIntro:
      "Managed shared hosting for small business sites. One account, multiple sites on higher tiers—SSL, backups, and monitoring included.",
    billing: "Billing",
    monthly: "Monthly",
    yearly: "Yearly",
    save20: "Save 20%",
    popular: "Popular",
    billedYearly: "billed yearly",
    cadence: "/mo",
    cancelAnytime: "Cancel anytime · No surprises",
    talkToSales: "Talk to Sales",
    metaTitle: "Managed Hosting for Small Business",
    metaDescription:
      "VPS servers and shared hosting for small business. Managed hosting with backups, monitoring, staging, and support.",
  },
  pricing: {
    subtitle: "Pricing",
    badge: "AI Integrations",
    title: "Pricing —",
    titleAccent: "possible ranges for AI Integrations",
    intro:
      "We scope each engagement to your goals and budget. The ranges below are indicative; your actual price depends on what you need—strategy, integrations, HIPAA forms, custom apps, or ongoing support.",
    rangesTitle: "Possible pricing ranges",
    rangesIntroBefore: "From",
    rangesIntroAfter:
      "for lighter or project-based work, scaling with scope (e.g. strategy, integrations, form setup, or ongoing support). Larger or custom engagements are quoted after a short discovery.",
    starterLabel: "Starter / one-off:",
    starterBody:
      "From {price}/month or project-based—e.g. a single integration, form setup, or a short strategy session.",
    ongoingLabel: "Ongoing support:",
    ongoingBody:
      "Monthly retainer options for ongoing AI integrations, form management, or support—priced to scope.",
    largerLabel: "Larger engagements:",
    largerBody:
      "Custom apps, multi-system integrations, or HIPAA/compliance-heavy work—we quote after discovery.",
    getQuote: "Get a quote",
    metaTitle: "AI Integrations Pricing",
    metaDescription:
      "CloudAxe AI Integrations pricing. We scope each engagement to fit your needs.",
  },
  help: {
    subtitle: "Help Center",
    eyebrow: "Help · Apps · Web Support · Hosting",
    title: "How can we help?",
    introBefore:
      "Search this page to quickly find guidance about Apps, Web Support, and Hosting. For anything urgent or unusual, you can still",
    contactLink: "contact us directly",
    introAfter: ".",
    searchLabel: "Search help",
    searchPlaceholder: "Search Apps, Web Support, Hosting…",
    filtering: "Filtering sections that mention your search.",
    noMatches: "No matches found in Apps, Web Support, or Hosting.",
    browseEyebrow: "Browse by area",
    quickHelp: "Quick help",
    browseIntro:
      "Jump into the area you care about most. Use the search above to narrow down what you see.",
    sectionAppsTitle: "Apps",
    sectionAppsDesc:
      "Find help for HIPAA-compliant forms, Event Maker, Rubric, and custom apps tailored to your workflows.",
    sectionAppsItems: [
      "Getting started with HIPAA-compliant forms",
      "Event Maker setup and event registration",
      "Rubric configuration and scoring",
      "Custom app onboarding and feature requests",
    ],
    sectionSupportTitle: "Web Support",
    sectionSupportDesc:
      "Support for WordPress, Next.js, and Joomla, plus DNS, SSL, email, and deployments.",
    sectionSupportItems: [
      "Troubleshooting common WordPress issues",
      "Next.js deployments and environment variables",
      "Joomla updates and extensions",
      "DNS, SSL, and email routing basics",
    ],
    sectionHostingTitle: "Hosting",
    sectionHostingDesc:
      "Guides for CloudAxe-managed hosting: performance, backups, and incident response.",
    sectionHostingItems: [
      "Understanding your hosting plan",
      "Backups, restore tests, and staging",
      "Performance tuning and caching",
      "What to do during an outage",
    ],
    metaTitle: "Help Center",
    metaDescription:
      "CloudAxe help for Apps, Web Support, and Hosting. Search guidance or contact us for urgent issues.",
  },
  faqs: {
    subtitle: "FAQs",
    eyebrow: "Help · FAQs",
    title: "Frequently asked questions",
    introBefore:
      "Common questions about Apps, Web Support, and Hosting. If you do not see your question here, you can always",
    contactLink: "contact us directly",
    introAfter: ".",
    metaTitle: "Frequently Asked Questions",
    metaDescription:
      "FAQs about CloudAxe Apps, Web Support, and Hosting for small business.",
  },
  contact: {
    subtitle: "Contact",
    eyebrow: "Get in touch",
    title: "Contact CloudAxe",
    intro:
      "Tell us what you're running and what you need—downtime, speed, security, email, deployments, or anything else. We'll respond with a plan and next steps.",
    chatTitle: "Chat",
    chatBody: "Reach us right now. Just click this button to start a new chat.",
    sendTitle: "Send a message",
    sendBody:
      "We typically respond within one business day. For hosting & migrations, tech support & troubleshooting, or app deployments & monitoring—describe your situation and we'll get back to you.",
    bulletHosting: "Hosting & migrations",
    bulletSupport: "Tech support & troubleshooting",
    bulletApps: "App deployments & monitoring",
    phoneTitle: "Phone",
    phoneBody: "Call us during business hours.",
    emailTitle: "Email",
    emailBody: "Prefer email? Send us a note and we'll reply within one business day.",
    emailGeneral: "support@cloudaxe.io — General & support",
    facebookTitle: "Facebook",
    facebookBody: "Follow us for service updates and announcements.",
    linkedinTitle: "LinkedIn",
    linkedinBody: "Connect with us for company news and updates.",
    whatsappTitle: "WhatsApp",
    whatsappBody: "Tap to start a chat with us on WhatsApp.",
    whatsappLink: "Chat with us on WhatsApp",
    metaTitle: "Contact Us",
    metaDescription:
      "Contact CloudAxe for Apps, Web Support, and Hosting. Tell us what you're running and what you need—we'll respond with a plan and next steps.",
  },
  intake: {
    submit: "Submit request",
    submitting: "Submitting…",
  },
};

export type Messages = typeof en;
